/**
 * API Communication Utility
 * Handles all API calls using fetch API
 */

export class API {
  /**
   * Make API request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<Object>}
   */
  static async request(endpoint, options = {}) {
    const {
      method = 'GET',
      body = null,
      headers = {},
      retries = 3,
      retryDelay = 1000
    } = options;

    const url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
      ...headers
    };

    // Remove Content-Type for FormData
    if (body instanceof FormData) {
      delete defaultHeaders['Content-Type'];
    }

    let lastError;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url, {
          method,
          headers: defaultHeaders,
          body: body instanceof FormData ? body : (body ? JSON.stringify(body) : null),
          credentials: 'same-origin'
        });

        // Handle both JSON and text responses
        const contentType = response.headers.get('content-type');
        let data;
        
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          try {
            data = JSON.parse(text);
          } catch (e) {
            data = { status: 'fail', error: text || 'Invalid response' };
          }
        }

        if (!response.ok && attempt < retries) {
          throw new Error(data.error || `HTTP ${response.status}`);
        }

        return data;
      } catch (error) {
        lastError = error;
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
        }
      }
    }

    throw lastError || new Error('Request failed');
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<Object>}
   */
  static async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Request options
   * @returns {Promise<Object>}
   */
  static async post(endpoint, body = null, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * Load form configuration
   * @param {string} formId - Form UUID
   * @param {string} behalfToken - Optional behalf token
   * @returns {Promise<Object>}
   */
  static async loadFormConfig(formId, behalfToken = null) {
    const body = { uuid: formId };
    if (behalfToken) {
      body.behalf_token = behalfToken;
    }
    return this.post('/tqtag/api/form_configuration', body);
  }

  /**
   * Load form entry (for edit mode)
   * @param {string} entryId - Entry UUID
   * @returns {Promise<Object>}
   */
  static async loadFormEntry(entryId) {
    return this.post('/tqtag/api/form_entry', { entry_id: entryId });
  }

  /**
   * Save form entry
   * @param {Object} data - Form data
   * @returns {Promise<Object>}
   */
  static async saveFormEntry(data) {
    return this.post('/tqtag/api/form_entry_save', data);
  }

  /**
   * Save form draft
   * @param {Object} data - Form data (will include behalf_token if present in URL)
   * @returns {Promise<Object>}
   */
  static async saveFormDraft(data) {
    // Extract behalf_token from URL query string if present (same as FormSubmitter does)
    const urlParams = new URLSearchParams(window.location.search);
    const behalfToken = urlParams.get('behalf_token');
    if (behalfToken) {
      data.behalf_token = behalfToken;
    }
    // Ensure draft flag is set
    data.draft = true;
    return this.post('/tqtag/api/form_entry_save_draft', data);
  }

  /**
   * Get last draft for a form
   * @param {string} formId - Form UUID
   * @param {string} behalfToken - Optional behalf token
   * @returns {Promise<Object>}
   */
  static async getLastDraft(formId, behalfToken = null, privateDraft = false) {
    const data = {
      uuid: formId,
      form: formId
    };
    if (behalfToken) {
      data.behalf_token = behalfToken;
    }
    if (privateDraft) {
      data.private_draft = true;
    }
    const response = await this.post('/tqtag/api/form_entry_get_last_draft', data);
    return response;
  }

  /**
   * List all drafts for a form
   * @param {string} formId - Form UUID
   * @param {string} behalfToken - Optional behalf token
   * @returns {Promise<Object>}
   */
  static async listDrafts(formId, behalfToken = null) {
    const data = {
      uuid: formId,
      form: formId
    };
    if (behalfToken) {
      data.behalf_token = behalfToken;
    }
    return this.post('/tqtag/api/form_entry_list_drafts', data);
  }

  /**
   * Get current user (IAM).
   * Sends optional JWT so backend can identify user when session cookie isn't used (e.g. JWT in localStorage).
   * @param {string|function|null} jwtOrGetter - JWT string, or function that returns JWT, or null to try window.TqTagJWT / localStorage 'tqtag_jwt'
   * @returns {Promise<Object>}
   */
  static async getCurrentUser(jwtOrGetter = null) {
    let jwt = null;
    if (jwtOrGetter != null) {
      jwt = typeof jwtOrGetter === 'function' ? jwtOrGetter() : jwtOrGetter;
    }
    if (!jwt && typeof window !== 'undefined') {
      jwt = window.TqTagJWT || (typeof localStorage !== 'undefined' ? localStorage.getItem('tqtag_jwt') : null) || null;
    }
    const headers = {};
    if (jwt) {
      headers['Authorization'] = `Bearer ${jwt}`;
    }
    return this.get('/tqtag/api/iam', { headers });
  }

  /**
   * Validate behalf token
   * @param {string} behalfToken - Behalf token from URL
   * @param {string} formId - Form UUID
   * @returns {Promise<Object>}
   */
  static async validateBehalfToken(behalfToken, formId) {
    return this.post('/tqtag/api/form_validate_behalf_token', {
      behalf_token: behalfToken,
      form_id: formId
    });
  }

  /**
   * Get form entries for the current user (or behalf customer when filling on behalf)
   * Used for field source "another form (user filled)"
   * @param {string|null} behalfToken - Optional behalf_token so API returns that customer's entries
   * @param {string|null} formId - Optional form uuid (required with behalfToken for token validation)
   * @returns {Promise<Object>} { status, entries: [{ form_id, form_data, date, ... }] }
   */
  static async getFormEntriesCurrentUser(behalfToken = null, formId = null) {
    const body = {};
    if (behalfToken && formId) {
      body.behalf_token = behalfToken;
      body.form = formId;
      body.uuid = formId;
    }
    return this.post('/tqtag/api/form_entries_current_user', body);
  }

  /**
   * Get customer by ID
   * @param {number} customerId - Customer ID
   * @returns {Promise<Object>}
   */
  static async getCustomerById(customerId) {
    return this.post('/tqtag/api/search_customers', {
      filter_id: customerId
    });
  }

  /**
   * Upload file
   * @param {FormData} formData - FormData with file
   * @param {Object} options - Upload options
   * @returns {Promise<Object>}
   */
  static async uploadFile(formData, options = {}) {
    return this.request('/', {
      method: 'POST',
      body: formData,
      ...options
    });
  }
}

